// aiOpponent.js
// Sprout, the greenhouse rival. Given the current state (with Sprout as the
// active player = index 1), returns ONE action at a time so the UI can play its
// turn out with readable pacing. Every non-"end" action consumes a card or a
// once-per-turn flag, so a turn always terminates.
//
// This is the "Closer + RotFix" brain, picked from an AI tournament (it beat the
// old greedy brain ~55/45 over thousands of mirror-team games):
//   • forecast-aware watering — won't top up a plant that rain will fill for
//     free or that sun will just dry back out;
//   • empty-pot-first — plants alongside an umbrella'd plant instead of digging
//     it up when a pot is open;
//   • a finish-the-bloom pass — uses any one action that completes a bloom this
//     turn (growth is swept after every action);
//   • RotFix — skips a Root Rot the upcoming weather would just undo.
// The ability hooks (droughtProof / drinksRain / extraDryInSun) are present but
// inert on this roster — SPECIES here carry no `ability`, so they no-op until an
// abilities system ships.

import { SPECIES, CARDS, WEATHER, MAX_LIGHT, MAX_WATER } from "./gameData";

const AI = 1;
const HUMAN = 0;

const clampL = (v) => Math.max(0, Math.min(MAX_LIGHT, v));
const clampW = (v) => Math.max(0, Math.min(MAX_WATER, v));

const lightGap = (p) => Math.max(0, SPECIES[p.speciesId].lightNeed - p.light);
const waterGap = (p) => {
  const sp = SPECIES[p.speciesId];
  if (p.water < sp.waterMin) return sp.waterMin - p.water;
  if (p.water > sp.waterMax) return p.water - sp.waterMax;
  return 0;
};
const distanceToGrow = (p) => lightGap(p) + waterGap(p);
const byClosest = (a, b) => distanceToGrow(a) - distanceToGrow(b);
// No abilities on this roster yet; kept as a hook so deploy order stays sensible
// if/when species gain abilities.
const abilityValue = () => 0;
const deployPriority = (id) => SPECIES[id].difficulty + abilityValue(id);

// ---- forecast model: what a given weather does to a specific plant ----
const waterDeltaFor = (plant, weatherId) => {
  if (plant.umbrella || plant.humidifier) return 0;
  let d = WEATHER[weatherId].water;
  const ab = SPECIES[plant.speciesId].ability;
  if (ab === "droughtProof" && weatherId === "sunny") d = 0;
  else if (ab === "drinksRain" && weatherId === "rain") d += 1;
  else if (ab === "extraDryInSun" && weatherId === "sunny") d -= 1;
  return d;
};
const lightDeltaFor = (plant, weatherId) => {
  let d = plant.umbrella ? 0 : WEATHER[weatherId].light;
  if (plant.growLamp) d += 1; // a grow lamp shines even through shade
  return d;
};
const afterWeather = (p, weatherId) => ({
  light: clampL(p.light + lightDeltaFor(p, weatherId)),
  water: clampW(p.water + waterDeltaFor(p, weatherId)),
});
const grownAt = (p, light, water) => {
  const sp = SPECIES[p.speciesId];
  return light >= sp.lightNeed && water >= sp.waterMin && water <= sp.waterMax;
};
const bloomsNow = (p, extraWater = 0, extraLight = 0) =>
  grownAt(p, clampL(p.light + extraLight), clampW(p.water + extraWater));

// A single action that completes a bloom THIS turn, if one exists.
const bloomFinisher = (me, mine) => {
  const inHand = (id) => me.hand.find((c) => c.id === id);
  const ready = (p) => p.light >= SPECIES[p.speciesId].lightNeed;

  if (!me.usedWater) {
    const p = mine.find((q) => !bloomsNow(q) && bloomsNow(q, 1));
    if (p) return { kind: "water", plantUid: p.uid };
  }
  for (const c of me.hand.filter((x) => CARDS[x.id].type === "plant")) {
    const host = mine.find((p) => p.speciesId === c.id && !bloomsNow(p) && bloomsNow(p, 1, 1));
    if (host) return { kind: "compost", handUid: c.uid, plantUid: host.uid };
  }
  const fert = inHand("fertilizer");
  if (fert) {
    const wet = mine.find((q) => !bloomsNow(q) && bloomsNow(q, 1, 1));
    if (wet) return { kind: "treatment", handUid: fert.uid, payload: { plantUid: wet.uid, water: 1 } };
    const dry = mine.find((q) => !bloomsNow(q) && bloomsNow(q, 0, 1));
    if (dry) return { kind: "treatment", handUid: fert.uid, payload: { plantUid: dry.uid, water: 0 } };
  }
  const irr = inHand("irrigation");
  if (irr) {
    for (const p of mine) {
      if (bloomsNow(p) || !ready(p)) continue;
      const sp = SPECIES[p.speciesId];
      for (let amt = -3; amt <= 3; amt++) {
        if (amt === 0) continue;
        const w = clampW(p.water + amt);
        if (w >= sp.waterMin && w <= sp.waterMax)
          return { kind: "treatment", handUid: irr.uid, payload: { plantUid: p.uid, amount: amt } };
      }
    }
  }
  return null;
};

export const chooseAiAction = (state) => {
  const me = state.players[AI];
  const mine = me.garden.filter(Boolean);
  const theirs = state.players[HUMAN].garden.filter(Boolean);
  const inHand = (id) => me.hand.find((c) => c.id === id);
  const f = state.nextForecast;

  /* ---- 0. Finish a bloom this turn if any single action does ---- */
  const finish = bloomFinisher(me, mine);
  if (finish) return finish;

  /* ---- 1. Blights: slow the human down (greedy order, with RotFix) ---- */
  const shovel = inHand("shovel");
  if (shovel) {
    const targets = theirs.filter((p) => p.light < 4 && p.water < 4);
    if (targets.length) {
      const t = (p) => SPECIES[p.speciesId].difficulty - abilityValue(p.speciesId);
      const best = targets.sort((a, b) => t(a) - t(b) || byClosest(a, b))[0];
      return { kind: "blight", handUid: shovel.uid, payload: { plantUid: best.uid } };
    }
  }
  const pests = inHand("pests");
  if (pests) {
    const juicy = theirs.filter((p) => p.light + p.water >= 5).sort(byClosest)[0];
    if (juicy) return { kind: "blight", handUid: pests.uid, payload: { plantUid: juicy.uid } };
  }
  const rot = inHand("rootRot");
  if (rot) {
    const rootRotSticks = (p) => {
      const sp = SPECIES[p.speciesId];
      const after = Math.min(10, p.water + 5);
      if (after === p.water || (after >= sp.waterMin && after <= sp.waterMax)) return false;
      // RotFix: don't bother if this turn's weather just drifts it back in.
      const wAfter = afterWeather({ ...p, water: after }, state.nextForecast).water;
      return wAfter < sp.waterMin || wAfter > sp.waterMax;
    };
    const target = theirs.filter(rootRotSticks).sort(byClosest)[0];
    if (target && distanceToGrow(target) <= 4) {
      return { kind: "blight", handUid: rot.uid, payload: { plantUid: target.uid } };
    }
  }
  const umbrella = inHand("umbrella");
  if (umbrella) {
    const target = theirs.filter((p) => !p.umbrella && lightGap(p) >= 1 && lightGap(p) <= 3).sort(byClosest)[0];
    if (target) return { kind: "blight", handUid: umbrella.uid, payload: { plantUid: target.uid } };
  }

  /* ---- 2. Plant action: deploy into empty pots; never dig prematurely ---- */
  if (!me.usedPlantAction) {
    const emptySlot = me.garden.findIndex((p) => p === null);
    const plantCards = me.hand.filter((c) => CARDS[c.id].type === "plant");
    const haveRepot = !!inHand("repot");
    // Only a truly drowned pot (far past any drain's reach) is worth digging.
    const drowned = mine
      .filter((p) => p.water > SPECIES[p.speciesId].waterMax + 4)
      .sort((a, b) => a.light + a.water - (b.light + b.water))[0];
    if (drowned) return { kind: "digUp", plantUid: drowned.uid };
    // Prefer planting into an open pot over anything else — an umbrella'd plant
    // beside a fresh one still beats throwing the umbrella'd one away.
    if (emptySlot !== -1 && plantCards.length) {
      const card = plantCards.sort((a, b) => deployPriority(b.id) - deployPriority(a.id))[0];
      return { kind: "plant", handUid: card.uid, slot: emptySlot };
    }
    // Garden full AND a fresh plant waiting: dig an umbrella'd dead-weight only
    // if we truly can't rescue it (can't repot the shade off, no progress).
    if (emptySlot === -1 && plantCards.length) {
      const stuck = mine
        .filter((p) => p.umbrella && p.light + p.water <= 2 && (p.light < 3 || !haveRepot))
        .sort((a, b) => a.light + a.water - (b.light + b.water))[0];
      if (stuck) return { kind: "digUp", plantUid: stuck.uid };
    }
  }

  /* ---- 3. Compost duplicate species onto established plants ---- */
  for (const card of me.hand.filter((c) => CARDS[c.id].type === "plant")) {
    const host = mine.find(
      (p) => p.speciesId === card.id && (lightGap(p) > 0 || p.water < SPECIES[p.speciesId].waterMax)
    );
    const gardenFull = me.garden.every((p) => p !== null);
    if (host && (gardenFull || me.usedPlantAction)) {
      return { kind: "compost", handUid: card.uid, plantUid: host.uid };
    }
  }

  /* ---- 4. Treatments ---- */
  const shopping = inHand("shopping");
  if (shopping && me.hand.length <= 3) return { kind: "treatment", handUid: shopping.uid, payload: {} };

  const lamp = inHand("growLamp");
  if (lamp) {
    const target = mine.filter((p) => !p.growLamp && lightGap(p) >= 2).sort((a, b) => lightGap(b) - lightGap(a))[0];
    if (target) return { kind: "treatment", handUid: lamp.uid, payload: { plantUid: target.uid } };
  }
  const fert = inHand("fertilizer");
  if (fert) {
    const target = mine.filter((p) => lightGap(p) >= 1).sort(byClosest)[0];
    if (target) {
      // Forecast patience: if rain is coming (or it's already full), take just
      // the light and skip the +1 💧 that might over-fill.
      const wet = f === "rain" || target.water >= SPECIES[target.speciesId].waterMax ? 0 : 1;
      return { kind: "treatment", handUid: fert.uid, payload: { plantUid: target.uid, water: wet } };
    }
  }
  const irrigation = inHand("irrigation");
  if (irrigation) {
    const thirsty = mine
      .filter((p) => SPECIES[p.speciesId].waterMin - p.water >= 2)
      // Patience: skip if the forecast refills it for free, or if it's far from
      // light-ready and the coming weather will just evaporate the pour.
      .filter((p) => !(f === "rain" && clampW(p.water + waterDeltaFor(p, "rain")) >= SPECIES[p.speciesId].waterMin))
      .filter((p) => !(lightGap(p) >= 3 && waterDeltaFor(p, f) < 0))
      .sort(byClosest)[0];
    if (thirsty) {
      const amount = Math.min(3, SPECIES[thirsty.speciesId].waterMin - thirsty.water);
      return { kind: "treatment", handUid: irrigation.uid, payload: { plantUid: thirsty.uid, amount } };
    }
    const flooded = mine.filter((p) => p.water - SPECIES[p.speciesId].waterMax >= 2).sort(byClosest)[0];
    if (flooded) {
      const amount = -Math.min(3, flooded.water - SPECIES[flooded.speciesId].waterMax);
      return { kind: "treatment", handUid: irrigation.uid, payload: { plantUid: flooded.uid, amount } };
    }
  }
  const humid = inHand("humidifier");
  if (humid) {
    const target = mine.find((p) => {
      const sp = SPECIES[p.speciesId];
      return !p.humidifier && p.water >= sp.waterMin && p.water <= sp.waterMax && lightGap(p) >= 1 && sp.waterMax - sp.waterMin <= 2;
    });
    if (target) return { kind: "treatment", handUid: humid.uid, payload: { plantUid: target.uid } };
  }
  const repot = inHand("repot");
  if (repot) {
    const target = mine.find((p) => p.light >= 3 && (p.umbrella || p.water > SPECIES[p.speciesId].waterMax + 1));
    if (target) {
      const sp = SPECIES[target.speciesId];
      const bonus = target.water > sp.waterMax + 1 ? "drain" : target.water < sp.waterMin ? "water" : "light";
      return { kind: "treatment", handUid: repot.uid, payload: { plantUid: target.uid, bonus } };
    }
  }
  const greenhouse = inHand("greenhouse");
  if (greenhouse) {
    const ops = [];
    for (const p of mine.sort(byClosest)) {
      if (ops.length >= 3) break;
      const sp = SPECIES[p.speciesId];
      if (!p.humidifier && p.water > sp.waterMax) ops.push({ plantUid: p.uid, stat: "water", delta: -1 });
      else if (lightGap(p) >= 1) ops.push({ plantUid: p.uid, stat: "light", delta: 1 });
    }
    if (ops.length >= 2) return { kind: "treatment", handUid: greenhouse.uid, payload: { ops } };
  }
  const recycle = inHand("recycle");
  if (recycle && me.hand.length <= 4) {
    const gem = me.discard.find((c) => ["fertilizer", "shopping", "irrigation", "growLamp"].includes(c.id));
    if (gem) return { kind: "treatment", handUid: recycle.uid, payload: { discardUid: gem.uid } };
  }
  const planter = inHand("planter");
  if (planter && !me.usedPlantAction && mine.length <= 1 && !me.hand.some((c) => CARDS[c.id].type === "plant")) {
    const slotIdx = me.garden.findIndex((p) => p === null);
    const deckPlant = me.deck
      .filter((c) => CARDS[c.id].type === "plant")
      .sort((a, b) => SPECIES[b.id].difficulty - SPECIES[a.id].difficulty)[0];
    if (slotIdx !== -1 && deckPlant) {
      return { kind: "treatment", handUid: planter.uid, payload: { deckUid: deckPlant.uid, slotIdx } };
    }
  }

  /* ---- 5. Watering: finish a bloom, else water only where it truly helps ---- */
  if (!me.usedWater) {
    const finisher = mine.filter((p) => !bloomsNow(p) && bloomsNow(p, 1)).sort(byClosest)[0];
    if (finisher) return { kind: "water", plantUid: finisher.uid };
    const target = mine
      .filter((p) => {
        const sp = SPECIES[p.speciesId];
        if (p.water >= sp.waterMin) return false; // not actually thirsty
        const proj = clampW(p.water + waterDeltaFor(p, f));
        if (f === "rain" && proj >= sp.waterMin && proj <= sp.waterMax) return false; // rain fills it free
        if (lightGap(p) >= 3 && waterDeltaFor(p, f) < 0) return false; // evaporates, far from ready
        return true;
      })
      .sort(byClosest)[0];
    if (target) return { kind: "water", plantUid: target.uid };
  }

  return { kind: "end" };
};
